import { useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $profileFeatureStore, $userAccountsStore } from '@lib/store';
import { cn } from '@shared/utils';
import type { UserResponse } from '@shared/api';
import { RemoveAccountsModal } from '../modals';
import { AccountTileSmall, AddAccountsModal, View } from '@widgets';
import { Button, Text } from '@shared/widgets';

interface AccountsProps {
  className?: string;
  user: UserResponse;
}

export const Accounts = (props: AccountsProps) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const userAccountsStore = useUnit($userAccountsStore.store);

  const [showAddAccounts, setShowAddAccounts] = useState<boolean>(false);
  const [showRemoveAccounts, setShowRemoveAccounts] = useState<boolean>(false);

  const { t } = useTranslation('user');

  return (
    <section className={cn(props.className)}>
      <div className='mb-6 flex flex-wrap items-center justify-between gap-4'>
        <Text weight='medium' size='2xl'>
          {t('accounts.title', { count: userAccountsStore.data.length })}
        </Text>
        <div className='flex items-center gap-3'>
          {profileFeatureStore.data?.userManagement?.managerAccountsOrCardsPermissions && (
            <Button color='outlined' onClick={() => setShowAddAccounts(true)}>
              {t('buttons.add', { ns: 'common' })}
            </Button>
          )}
          {userAccountsStore.data.length > 0 && (
            <Button color='outlined' onClick={() => setShowRemoveAccounts(true)}>
              {t('buttons.remove', { ns: 'common' })}
            </Button>
          )}
        </div>
      </div>
      <View
        loading={userAccountsStore.loading}
        error={userAccountsStore.error}
        onRefresh={() => $userAccountsStore.events.getUserAccounts({ AssignedUserId: props.user.id })}
        noData={!userAccountsStore.data.length}
        noDataText={t('noData.noRecords', { ns: 'common' })}
      >
        <div className={cn('flex flex-wrap gap-[30px]', props.user.locked && 'pointer-events-none select-none opacity-40')}>
          {userAccountsStore.data.map((item, idx) => (
            <AccountTileSmall key={idx} className={cn('w-[calc(50%-15px)]', 'max-[1400px]:w-full')} account={item} />
          ))}
        </div>
      </View>
      <AddAccountsModal
        isOpen={showAddAccounts}
        onClose={() => setShowAddAccounts(false)}
        user={props.user}
        accountsItems={userAccountsStore.data}
        onSubmit={(data) => $userAccountsStore.events.addAccounts(data)}
      />
      <RemoveAccountsModal show={showRemoveAccounts} userData={props.user} onClose={() => setShowRemoveAccounts(false)} />
    </section>
  );
};
